import { useEffect, useState } from 'react';
import { Flex, Button, Image, ResponsiveAcuityInfoPanel, Text, theme } from '../../../2022-component-library';
import { useTestContext } from '../../../context/testContext';
import { getRandomEScore } from '../../../helpers/functions';
import { useCalibration } from '../../../context/calibrationContext';
import { usePostMessage, useReceiveMessage } from '../../../context/messageContext';
import { useLogmarData } from '../../../helpers/reducers';
import { useLogmarSizeCalculations } from '../../../helpers/useLogmarSizeCalculations';
import { useMediaQuery } from '../../../helpers/useMediaQuery';

const ResponsiveKaysOptotypesTestOptician = ({ eyeTested, goToTestResults }) => {
  const {
    calibrationData: { distanceFromScreen, methodSelected },
    updateCalibrationData,
  } = useCalibration();
  const [optotypes, setOptotypes] = useState([]);
  const { testData, setTestFieldValue, setMultipleTestFieldValues, updateTestData } = useTestContext();
  const [testMode, setTestMode] = useState(methodSelected !== 'manual' ? 'auto' : testData.testMode);
  const [currentLogmar, setCurrentLogmar] = useState(testData?.eyeTested);
  const [patientMoving, setPatientMoving] = useState(false);
  const [currentOptotypeIndex, setCurrentOptotypeIndex] = useState(0);
  const [incorrectSizeCounter, setIncorrectSizeCounter] = useState({ logmarId: 1, counter: 0 });
  const postMessage = usePostMessage();
  const [initialLoad, setInitialLoad] = useState(false);
  const messageFromPatient = useReceiveMessage();
  const { currentLogmarData, updateLogmarData } = useLogmarData();
  const { getContentSize } = useLogmarSizeCalculations();
  const smallScreen = useMediaQuery('(max-width: 700px)');

  useEffect(() => {
    updateTestData({ testInProgress: true });
  }, [updateTestData]);

  const changeTestMode = (selectedTestMode) => {
    if (selectedTestMode !== testMode) {
      changeTestModeData(selectedTestMode);
    }
  };

  const changeTestModeData = (selectedTestMode) => {
    let newValues = testData;
    newValues.testMode = selectedTestMode;
    setMultipleTestFieldValues(newValues);
    setTestMode(selectedTestMode);
    postMessage({
      type: 'updateTestMode',
      content: {
        testMode: selectedTestMode,
      },
    });
  };

  useEffect(() => {
    const importOptotypes = (src) => src.keys().map(src);
    const optotypes = importOptotypes(require.context('../../../assets/kays-optotypes', false, /\.svg$/));
    setOptotypes(optotypes);
  }, []);

  const selectNextLogmar = () => {
    if (currentLogmar.id === 13) return finishTest();
    return updateLogmarData({ type: 'decrementLogmarSize' });
  };

  const selectPreviousLogmar = () => {
    if (currentLogmar.id > 1) return updateLogmarData({ type: 'incrementLogmarSize' });
  };

  const markIncorrectAnswer = () => {
    if (currentLogmar.id === incorrectSizeCounter['logmarId']) {
      if (incorrectSizeCounter['counter'] > 0) {
        finishTest();
      }
    }
    setIncorrectSizeCounter({ logmarId: currentLogmar.id, counter: 1 });
    selectPreviousLogmar();
  };

  const markRepeatAnswer = () => {
    updateLogmarContentSize();
  };

  const changeOptotype = () => {
    const getRandomIndex = () => {
      const randomIndex = Math.floor(Math.random() * 6);
      return randomIndex !== currentOptotypeIndex ? randomIndex : getRandomIndex();
    };

    const randomIndex = getRandomIndex();
    return randomIndex;
  };

  const finishTest = () => {
    let logmarForScore = currentLogmar.id - 1;
    if (currentLogmar.id === 13) {
      logmarForScore = 13;
    }
    const score = { score: getRandomEScore(logmarForScore) };
    setTestFieldValue({
      name: eyeTested,
      value: score,
    });
    goToTestResults();
  };

  const updateLogmarContentSize = (logmar, distance) => {
    const contentSize = Math.round(getContentSize(logmar.id, distance));
    const newOptotypeIndex = changeOptotype();
    setCurrentOptotypeIndex(newOptotypeIndex);
    postMessage({
      type: 'resizeOptotype',
      content: {
        contentSize: contentSize,
        distanceReferenceInMM: distance,
        currentOptotypeIndex: newOptotypeIndex,
      },
    });
  };

  useEffect(() => {
    if (!initialLoad) {
      setPatientMoving(false);
      updateLogmarData({ type: 'setInitialLogmarSize', payload: testData.eyeTested });
      postMessage({
        type: 'kaysOptotypes',
        content: {
          distanceFromScreen: distanceFromScreen,
          methodSelected: methodSelected,
          testMode: testMode,
          crowding: testData.kaysCrowding,
        },
      });

      setInitialLoad(true);
    }
  }, [initialLoad, eyeTested, postMessage, testData, updateLogmarData]);

  useEffect(() => {
    const logmar = currentLogmarData.logmarSize;
    setCurrentLogmar(logmar);
    updateLogmarContentSize(logmar, distanceFromScreen);
  }, [currentLogmarData.logmarSize]);

  useEffect(() => {
    if (messageFromPatient.type === 'patientMovementDetected') {
      setPatientMoving(messageFromPatient.content.patientIsMoving);
      if (messageFromPatient.content.patientDistance) updateCalibrationData({ distanceFromScreen: messageFromPatient.content.patientDistance });

      if (messageFromPatient.content.patientIsMoving === false) updateLogmarContentSize(currentLogmar, messageFromPatient.content.patientDistance);
    }
  }, [messageFromPatient, updateCalibrationData]);

  return (
    <Flex centered column width='85vw' maxWidth='700px'>
      <Flex width='100%' justifyContent='space-between' alignItems={!smallScreen && 'center'}>
        <Flex
          centered
          border={`1px solid ${theme.colors.neutralLight}`}
          borderRadius='4px'
          width='55vw'
          height='50vh'
          maxHeight='400px'
          marginBottom='1rem'
          marginRight='1rem'
        >
          <Image
            src={optotypes[currentOptotypeIndex]?.default}
            alt='random-e'
            width='35vh'
            height='35vh'
            maxHeight='200px'
            maxWidth='200px'
            style={{ ...(testData?.kaysCrowding === 'crowded' && { borderStyle: 'solid', borderWidth: '1vw', padding: '5vw' }) }}
          />
        </Flex>
        <Flex column height='50vh' maxHeight='200px' width='30vw' justifyContent='space-between' alignItems='center'>
          <Flex centered borderBottom='1px solid black' width='100%' padding='0.25rem 0'>
            <Text>{`logMAR Size: ${currentLogmar?.value?.toFixed(1) || 0}`}</Text>
          </Flex>
          <Button width='15em' maxWidth='30vw' onClick={() => selectNextLogmar()}>
            Correct
          </Button>
          <Button width='15em' maxWidth='30vw' backgroundColor={theme.colors.negativeDark} onClick={() => markIncorrectAnswer()}>
            Incorrect
          </Button>
          <Button width='15em' maxWidth='30vw' backgroundColor={theme.colors.callToAction} onClick={() => markRepeatAnswer()}>
            Repeat
          </Button>
        </Flex>
      </Flex>
      <ResponsiveAcuityInfoPanel changeTestMode={changeTestMode} />
    </Flex>
  );
};

export default ResponsiveKaysOptotypesTestOptician;
